

import { useEffect, useState } from "react";
import { Banner, Button, Field, Input, Select } from "@/shared/rd";

import { useTranslations } from "@/lib/i18n/intl";
import type { SubscriptionPlanView } from "@/lib/api/types";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useAssignStoreSubscriptionMutation } from "@/modules/stores/services/mutations/useAssignStoreSubscriptionMutation";

type Props = {
  storeId: string;
  plans: SubscriptionPlanView[];
  locale: Locale;
  /** After the plan is assigned, with its name for the toast. */
  onSuccess?: (planName: string) => void;
  /** Lets a drawer put the submit in its own bar (`form={formId}`). */
  formId?: string;
  hideSubmit?: boolean;
  onBusyChange?: (busy: boolean) => void;
};

export function StoreSubscriptionForm({
  storeId,
  plans,
  locale,
  onSuccess,
  formId = `assign-subscription-${storeId}`,
  hideSubmit,
  onBusyChange,
}: Props) {
  const t = useTranslations();
  /** The server refused: a banner at the top of the form. */
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ plan?: string; amount?: string; period?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [planId, setPlanId] = useState("");
  const { mutateAsync: assignSubscription } = useAssignStoreSubscriptionMutation();

  useEffect(() => {
    onBusyChange?.(isSubmitting);
  }, [isSubmitting, onBusyChange]);

  const options = [
    ...plans.map((plan) => ({
      value: plan.id,
      label: pickLocalized(locale, plan.nameAr, plan.nameEn),
    })),
  ];

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setError(null);
    setFieldErrors({});
    const formData = new FormData(form);
    const nextErrors: { plan?: string; amount?: string; period?: string } = {};
    if (planId.trim() === "") {
      nextErrors.plan = t(keys.errors.required);
    }

    const startDate = formData.get("startDate");
    const overrideAmount = formData.get("overrideAmount");
    const overridePeriodDays = formData.get("overridePeriodDays");
    const parsedOverrideAmount =
      typeof overrideAmount === "string" && overrideAmount.trim() !== ""
        ? Number(overrideAmount)
        : undefined;
    if (
      parsedOverrideAmount !== undefined &&
      (!Number.isFinite(parsedOverrideAmount) || parsedOverrideAmount < 0)
    ) {
      nextErrors.amount = t(keys.admin.subscriptionPaymentAmountInvalid);
    }
    const parsedOverridePeriodDays =
      typeof overridePeriodDays === "string" && overridePeriodDays.trim() !== ""
        ? Number(overridePeriodDays)
        : undefined;
    if (
      parsedOverridePeriodDays !== undefined &&
      (!Number.isFinite(parsedOverridePeriodDays) || parsedOverridePeriodDays <= 0)
    ) {
      nextErrors.period = t("admin.subscriptionPeriodInvalid");
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await assignSubscription({
        storeId,
        planId,
        startDate: typeof startDate === "string" && startDate ? startDate : undefined,
        overrideAmount: parsedOverrideAmount,
        overridePeriodDays: parsedOverridePeriodDays,
      });
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    const plan = plans.find((item) => item.id === planId);
    form.reset();
    setPlanId("");
    setIsSubmitting(false);
    onSuccess?.(plan ? pickLocalized(locale, plan.nameAr, plan.nameEn) : "");
  };

  return (
    <form id={formId} style={{ display: "flex", flexDirection: "column", gap: 14 }} onSubmit={handleSubmit}>
      {error ? (
        <Banner tone="error" title={t("admin.subscriptionAssignFailedTitle")}>
          {error} {t("admin.nothingChanged")}
        </Banner>
      ) : null}
      <Field label={t(keys.admin.subscriptionSelectLabel)} htmlFor={`${formId}-plan`} error={fieldErrors.plan}>
        <Select
          id={`${formId}-plan`}
          value={planId}
          onChange={(event) => setPlanId(event.target.value)}
          options={options}
          placeholder={t(keys.admin.subscriptionSelectPlaceholder)}
          style={{ maxWidth: 360 }}
        />
      </Field>
      <div className="rd-fieldset">
        <Field
          label={t(keys.admin.subscriptionStartDateLabel)}
          htmlFor={`${formId}-start`}
          help={t(keys.admin.subscriptionStartDateHelper)}
        >
          <Input id={`${formId}-start`} name="startDate" type="date" style={{ width: 180 }} />
        </Field>
        <div className="rd-fields-inline">
          <Field
            label={t(keys.admin.subscriptionOverrideAmountLabel)}
            htmlFor={`${formId}-amount`}
            help={t(keys.admin.subscriptionOverrideAmountHelper)}
            error={fieldErrors.amount}
          >
            <Input
              id={`${formId}-amount`}
              name="overrideAmount"
              type="number"
              inputMode="decimal"
              placeholder="0"
              step="0.01"
              style={{ width: 160 }}
            />
          </Field>
          <Field
            label={t(keys.admin.subscriptionOverridePeriodLabel)}
            htmlFor={`${formId}-period`}
            help={t(keys.admin.subscriptionOverridePeriodHelper)}
            error={fieldErrors.period}
          >
            <Input id={`${formId}-period`} name="overridePeriodDays" type="number" placeholder="30" style={{ width: 130 }} />
          </Field>
        </div>
      </div>
      {hideSubmit ? null : (
        <div>
          <Button type="submit" variant="primary" loading={isSubmitting} loadingLabel={t("admin.busyAssigning")}>
            {t(keys.admin.subscriptionAssignAction)}
          </Button>
        </div>
      )}
    </form>
  );
}
